import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api';

const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserPermissions();
  }, []);

  const loadUserPermissions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        setLoading(false);
        return;
      }

      const response = await api.get('/me');

      if (response.data.success) {
        const userData = response.data.user;
        setUser(userData);
        setRoles(userData.roles || []);
        setPermissions(userData.permissions || []);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
      // If token is invalid, clear it
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
      }
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (permission) => {
    if (!permission) return true; // No permission required
    if (roles.includes('super_admin')) return true; // Super admin has all permissions
    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissionsList) => {
    if (!permissionsList || permissionsList.length === 0) return true;
    if (roles.includes('super_admin')) return true;
    return permissionsList.some(p => permissions.includes(p));
  };

  const hasAllPermissions = (permissionsList) => {
    if (!permissionsList || permissionsList.length === 0) return true;
    if (roles.includes('super_admin')) return true;
    return permissionsList.every(p => permissions.includes(p));
  };

  const hasRole = (role) => {
    if (!role) return true;
    return roles.includes(role);
  };

  const hasAnyRole = (rolesList) => {
    if (!rolesList || rolesList.length === 0) return true;
    return rolesList.some(role => roles.includes(role));
  };

  const hasAllRoles = (rolesList) => {
    if (!rolesList || rolesList.length === 0) return true;
    return rolesList.every(role => roles.includes(role));
  };

  const isSuperAdmin = () => {
    return roles.includes('super_admin');
  };

  const value = {
    user,
    permissions,
    roles,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    isSuperAdmin,
    reload: loadUserPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context;
};

export default PermissionContext;
